import { useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";

// 지표 설명처럼 "평소엔 안 보여도 되지만 필요할 때 바로 찾을 수 있어야 하는" 텍스트를 ⓘ
// 아이콘 뒤에 접어둔다(2026-07-17). 클릭하면 열리고 바깥을 클릭하면 닫힌다.
//
// 구조화 리디자인(2026-07-18 피드백 — "벽글이라 한눈에 안 들어온다, 쪼개 달라"):
// 검은 박스에 문단 하나 대신, 흰 카드에 [용어 → 한 줄 설명] 목록으로 쪼개서 원하는
// 항목만 골라 읽게 한다. 화살표 페이지 넘김도 검토했지만 넘기는 동안 앞 내용을 기억해야
// 해서 오히려 불친절 — 스캔 가능한 목록이 정답. items: [{ term, desc }], intro 는 선택.
// text: children 대용 문자열 prop(간단 설명용). children | text | items 셋 중 하나로 내용 전달.
const _TIP_W = 320; // w-80


export default function InfoTooltip({ children, intro, items, text }) {
  const body = children ?? text;
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    // 공간 있는 쪽으로 연다 — left-0(오른쪽으로 펼침)이 뷰포트 오른쪽을 넘으면 right-0(왼쪽으로).
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setAlignRight(r.left + _TIP_W > window.innerWidth - 16 && r.right - _TIP_W > 16);
    }
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  return (
    <span className="relative inline-flex" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-ink-400 hover:bg-ink-100 hover:text-ink-600 ${open ? "bg-ink-100 text-ink-600" : ""}`}
        aria-label="설명 보기"
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className={`absolute ${alignRight ? "right-0" : "left-0"} top-full z-30 mt-1.5 w-80 max-w-[calc(100vw-2.5rem)] rounded-xl border border-ink-100 bg-white p-2 text-xs font-normal leading-relaxed shadow-[0_8px_24px_rgba(15,23,42,0.14)]`}>
          {intro && <p className="px-2 pb-1.5 pt-1 text-ink-600">{intro}</p>}
          {items ? (
            <div className="divide-y divide-ink-50">
              {/* 항목 호버 — 옅은 파랑이 스치듯 들어오고 살짝 밀리는 정도만(꾸안꾸,
                  2026-07-18 피드백). 읽는 항목이 어디인지 시선을 잡아준다. */}
              {items.map((it) => (
                <div
                  key={it.term}
                  className="rounded-lg px-2 py-1.5 transition-all duration-200 hover:translate-x-0.5 hover:bg-brand-50/60"
                >
                  <div className="font-semibold text-ink-800">{it.term}</div>
                  <div className="mt-0.5 text-ink-500">{it.desc}</div>
                </div>
              ))}
            </div>
          ) : (
            body && <p className="px-2 py-1 text-ink-600">{body}</p>
          )}
        </div>
      )}
    </span>
  );
}
